import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Command } from 'cmdk';
import {
  Home,
  Users,
  CheckSquare,
  Calendar,
  Settings,
  Plus,
  Sun,
  Moon,
  Monitor,
  Languages,
} from 'lucide-react';

import { useUIStore } from '@/stores/ui-store';
import { Dialog, DialogContent } from '@/components/ui/dialog';

export function CommandPalette() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const {
    commandOpen,
    setCommandOpen,
    setTheme,
    setLanguage,
    setCreateTaskOpen,
    setCreateStudentOpen,
    setCreateEventOpen,
  } = useUIStore();

  // Keyboard shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setCommandOpen(!commandOpen);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [commandOpen, setCommandOpen]);

  const runCommand = useCallback(
    (command: () => void) => {
      setCommandOpen(false);
      command();
    },
    [setCommandOpen]
  );

  return (
    <Dialog open={commandOpen} onOpenChange={setCommandOpen}>
      <DialogContent className="overflow-hidden p-0 shadow-lg max-w-[500px]">
        <Command className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          <Command.Input
            placeholder={t('command.searchPlaceholder')}
            className="flex h-11 w-full rounded-md bg-transparent py-3 px-4 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 border-b"
          />
          <Command.List className="max-h-[400px] overflow-y-auto overflow-x-hidden p-2">
            <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
              {t('command.noResults')}
            </Command.Empty>

            {/* Navigation */}
            <Command.Group heading={t('command.navigation')}>
              <Command.Item
                onSelect={() => runCommand(() => navigate('/'))}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground"
              >
                <Home className="h-4 w-4" />
                <span>{t('nav.dashboard')}</span>
              </Command.Item>
              <Command.Item
                onSelect={() => runCommand(() => navigate('/students'))}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground"
              >
                <Users className="h-4 w-4" />
                <span>{t('nav.students')}</span>
              </Command.Item>
              <Command.Item
                onSelect={() => runCommand(() => navigate('/tasks'))}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground"
              >
                <CheckSquare className="h-4 w-4" />
                <span>{t('nav.tasks')}</span>
              </Command.Item>
              <Command.Item
                onSelect={() => runCommand(() => navigate('/calendar'))}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground"
              >
                <Calendar className="h-4 w-4" />
                <span>{t('nav.calendar')}</span>
              </Command.Item>
              <Command.Item
                onSelect={() => runCommand(() => navigate('/settings'))}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground"
              >
                <Settings className="h-4 w-4" />
                <span>{t('nav.settings')}</span>
              </Command.Item>
            </Command.Group>

            {/* Actions */}
            <Command.Group heading={t('command.actions')}>
              <Command.Item
                onSelect={() => runCommand(() => setCreateTaskOpen(true))}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground"
              >
                <Plus className="h-4 w-4" />
                <span>{t('command.newTask')}</span>
              </Command.Item>
              <Command.Item
                onSelect={() => runCommand(() => setCreateStudentOpen(true))}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground"
              >
                <Plus className="h-4 w-4" />
                <span>{t('command.newStudent')}</span>
              </Command.Item>
              <Command.Item
                onSelect={() => runCommand(() => setCreateEventOpen(true))}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground"
              >
                <Plus className="h-4 w-4" />
                <span>{t('command.newEvent')}</span>
              </Command.Item>
            </Command.Group>

            {/* Theme */}
            <Command.Group heading={t('command.theme')}>
              <Command.Item
                onSelect={() => runCommand(() => setTheme('light'))}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground"
              >
                <Sun className="h-4 w-4" />
                <span>{t('theme.light')}</span>
              </Command.Item>
              <Command.Item
                onSelect={() => runCommand(() => setTheme('dark'))}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground"
              >
                <Moon className="h-4 w-4" />
                <span>{t('theme.dark')}</span>
              </Command.Item>
              <Command.Item
                onSelect={() => runCommand(() => setTheme('system'))}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground"
              >
                <Monitor className="h-4 w-4" />
                <span>{t('theme.system')}</span>
              </Command.Item>
            </Command.Group>

            {/* Language */}
            <Command.Group heading={t('command.language')}>
              <Command.Item
                onSelect={() => runCommand(() => setLanguage('es'))}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground"
              >
                <Languages className="h-4 w-4" />
                <span>{t('language.es')}</span>
              </Command.Item>
              <Command.Item
                onSelect={() => runCommand(() => setLanguage('en'))}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none aria-selected:bg-accent aria-selected:text-accent-foreground"
              >
                <Languages className="h-4 w-4" />
                <span>{t('language.en')}</span>
              </Command.Item>
            </Command.Group>
          </Command.List>

          <div className="border-t px-3 py-2 text-xs text-muted-foreground">
            <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100">
              <span className="text-xs">⌘</span>K
            </kbd>
            <span className="ml-2">{t('command.hint')}</span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
